import { __decorate, __metadata } from "tslib";
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppEnvironment } from '@ho/shared/data-access/core/app-environment.model';
import { permissionsList } from '@ho/shared/widgets/permission-settings';
import { DEFAULT_SCHEMES, UiSettingsService } from '@ho/shared/data-access/ui-settings';
import { SettingsService } from '../../../../core/src/lib/+state/settings.service';
import { ThemeSettingsService } from '../../../../ui-settings/src/lib/services/theme-settings.service';
import { SessionApiService } from './session-api.service';
import { UserTypeSystemMap, UserType } from './session-user-type-service.service';
import { SessionQuery } from './session.query';
import { SessionStore } from './session.store';
import { CommissionType } from '@ho/shared/data-access/dictionary';
import { ALL_PERMISSIONS } from '@ho/tools/constants';
let SessionService = class SessionService {
    constructor(_ngxPermissionService, _authStore, _authQuery, _sessionApiService, _http, _jwtHelper, _uiSettingsService, _system, _translate, _themeSettingsService, _environment, _document, environment) {
        this._ngxPermissionService = _ngxPermissionService;
        this._authStore = _authStore;
        this._authQuery = _authQuery;
        this._sessionApiService = _sessionApiService;
        this._http = _http;
        this._jwtHelper = _jwtHelper;
        this._uiSettingsService = _uiSettingsService;
        this._system = _system;
        this._translate = _translate;
        this._themeSettingsService = _themeSettingsService;
        this._environment = _environment;
        this._document = _document;
        this.environment = environment;
    }
    autologin() {
        return this._sessionApiService.currentUserInfo().pipe(switchMap((_) => {
            return this._gettingUserInfo(this._prepareUser(_));
        }));
    }
    login(credentials) {
        return this._sessionApiService.login(credentials).pipe(switchMap((_) => {
            return this._gettingUserInfo(this._prepareUser(_));
        }));
    }
    updateUserInfo(userInfo) {
        this._authStore.update((_) => {
            return Object.assign(Object.assign({}, _), { userInfo });
        });
    }
    clearUserInfo() {
        this._authStore.update((_) => {
            return {
                userInfo: null,
            };
        });
    }
    logout() {
        return this._sessionApiService.logout().pipe(tap(() => {
            this._authStore.reset();
            this._ngxPermissionService.flushPermissions();
            localStorage.clear();
        }));
    }
    save(body, options) {
        return this._http.put(`/me`, body, options);
    }
    update(body, id) {
        return this._http.patch(`/user_profiles/${id}`, body);
    }
    processingUserPermissions(permissions) {
        permissionsList.forEach((permissionBlock) => {
            permissionBlock.permissions.forEach((permission) => {
                this._ngxPermissionService.addPermission(permission.key, () => {
                    return permissions.includes(permission.key);
                });
            });
        });
        this._enableCustomPermissions();
    }
    switchApp(observer, userType) {
        var _a, _b;
        switch (userType || ((_a = this._authQuery.userInfo()) === null || _a === void 0 ? void 0 : _a.user_type)) {
            case UserType.Admin:
            case UserType.Affiliate:
            case UserType.Manager:
                window.location.href = `${location.origin}/${this.getPathByUserType(userType || ((_b = this._authQuery.userInfo()) === null || _b === void 0 ? void 0 : _b.user_type))}/d/`;
                break;
            default:
                window.location.href = location.href.includes('localhost')
                    ? `${location.origin}/${UserTypeSystemMap.get(this.environment.role[0]) || 'auth/a/'}`
                    : `${location.origin}/auth/a/`;
        }
        if (observer) {
            observer.next(false);
            observer.complete();
        }
        return of(false);
    }
    getPathByUserType(userType) {
        switch (userType) {
            case UserType.Admin:
                return 'admin';
            case UserType.Affiliate:
                return 'affiliate';
            case UserType.Manager:
                return 'manager';
            default:
                return 'auth';
        }
    }
    _gettingUserInfo(currentUser) {
        return of({}).pipe(switchMap((_) => (currentUser ? of(currentUser) : this._sessionApiService.currentUserInfo())), tap((response) => {
            this._authStore.update((state) => {
                return Object.assign(Object.assign({}, state), { userInfo: response });
            });
            this._translate.use('en');
        }), switchMap((r) => {
            return this._uiSettingsService.getUiSettings();
        }), switchMap((r) => {
            this._themeSettingsService.applyThemes(Object.assign(Object.assign({}, DEFAULT_SCHEMES), r.theme_settings));
            // because auth-app is not separate application in dev mode, permission initializer in other case
            return !this._environment.production ? this._sessionApiService.myPermissions() : of(null);
        }), tap((myPermissions) => {
            if (!myPermissions) {
                return;
            }
            this.processingUserPermissions(myPermissions);
            this._authStore.update((state) => {
                return Object.assign(Object.assign({}, state), { userInfo: Object.assign(Object.assign({}, this._authQuery.userInfo()), { permissions: myPermissions }) });
            });
        }));
    }
    _prepareUser(user) {
        return Object.assign({ email: 'test@test.test', full_name: user.first_name + ' ' + user.last_name, registration_completed: true, commission_threshold: 0, time_zone: null, permissions: ALL_PERMISSIONS, email_notifications: ['country_capacity_reached', 'tracking_domain_removed'], language: 'en', alert_notifications: [], commission_type_id: CommissionType.CPA }, user);
    }
    _enableCustomPermissions() {
        Promise.all([
            this._ngxPermissionService.hasPermission('UI_AdvertiserBilling_View'),
            this._ngxPermissionService.hasPermission('UI_UserOperations_View'),
            this._ngxPermissionService.hasPermission('UI_UserOperations_CreateUser'),
            this._ngxPermissionService.hasPermission('UI_ClickPayoutRules_View'),
            this._ngxPermissionService.hasPermission('UI_ClickBilling_View'),
            this._ngxPermissionService.hasPermission('UI_PushBrokers_View'),
            this._ngxPermissionService.hasPermission('UI_CRG_View'),
            this._ngxPermissionService.hasPermission('UI_EPL_View'),
        ]).then(([UI_AdvertiserBilling_View, UI_UserOperations_View, UI_UserOperations_CreateUser, UI_ClickPayoutRules_View, UI_ClickBilling_View, UI_PushBrokers_View, UI_CRG_View, UI_EPL_View,]) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            if ((_a = this._system.settings) === null || _a === void 0 ? void 0 : _a.workspace_features.includes('cpa_network_solution')) {
                this._ngxPermissionService.addPermission('UI_Enable_Advertiser');
                if (UI_AdvertiserBilling_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_DISPLAY_ADVERTISER_BILLING');
                }
                if (UI_UserOperations_CreateUser) {
                    this._ngxPermissionService.addPermission('CUSTOM_CREATE_ADVERTISER');
                }
                if (UI_UserOperations_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_ARCHIVES_ADVERTISER');
                }
            }
            if ((_b = this._system.settings) === null || _b === void 0 ? void 0 : _b.workspace_features.includes('click_commissions')) {
                this._ngxPermissionService.addPermission('CUSTOM_CLICK_COMMISSIONS', () => true);
                if (UI_ClickPayoutRules_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_UI_ClickPayoutRules_View', () => true);
                }
                if (UI_ClickBilling_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_UI_ClickBilling_View', () => true);
                }
            }
            if ((_c = this._system.settings) === null || _c === void 0 ? void 0 : _c.workspace_features.includes('automatically_create_revenue_rule')) {
                this._ngxPermissionService.addPermission('CUSTOM_AUTOMATICALLY_CREATE_REVENUE_RULE', () => true);
            }
            if ((_d = this._system.settings) === null || _d === void 0 ? void 0 : _d.workspace_features.includes('push_brokers')) {
                if (UI_PushBrokers_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_UI_PushBrokers_View', () => true);
                }
            }
            if ((_e = this._system.settings) === null || _e === void 0 ? void 0 : _e.workspace_features.includes('crg')) {
                if (UI_CRG_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_UI_CRG_View', () => true);
                }
            }
            if ((_f = this._system.settings) === null || _f === void 0 ? void 0 : _f.workspace_features.includes('epl')) {
                if (UI_EPL_View) {
                    this._ngxPermissionService.addPermission('CUSTOM_UI_EPL_View', () => true);
                }
            }
            if ((_g = this._system.settings) === null || _g === void 0 ? void 0 : _g.workspace_features.includes('hide_revenue')) {
                this._ngxPermissionService.addPermission('CUSTOM_UI_Hide_Revenue_View', () => true);
            }
            if (((_h = this._authQuery.userInfo()) === null || _h === void 0 ? void 0 : _h.user_type) === UserType.Admin) {
                this._ngxPermissionService.addPermission('CUSTOM_UI_Admin', () => true);
            }
            if (((_j = this._authQuery.userInfo()) === null || _j === void 0 ? void 0 : _j.user_type) === UserType.Affiliate) {
                this._ngxPermissionService.addPermission('CUSTOM_UI_Affiliate', () => true);
            }
            if (((_k = this._authQuery.userInfo()) === null || _k === void 0 ? void 0 : _k.user_type) === UserType.Manager) {
                this._ngxPermissionService.addPermission('CUSTOM_UI_Manager', () => true);
            }
        });
    }
};
SessionService.ctorParameters = () => [
    { type: NgxPermissionsService },
    { type: SessionStore },
    { type: SessionQuery },
    { type: SessionApiService },
    { type: HttpClient },
    { type: JwtHelperService },
    { type: UiSettingsService },
    { type: SettingsService },
    { type: TranslateService },
    { type: ThemeSettingsService },
    { type: undefined, decorators: [{ type: Inject, args: [AppEnvironment,] }] },
    { type: Document, decorators: [{ type: Inject, args: [DOCUMENT,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [AppEnvironment,] }] }
];
SessionService = __decorate([
    Injectable(),
    __metadata("design:paramtypes", [NgxPermissionsService,
        SessionStore,
        SessionQuery,
        SessionApiService,
        HttpClient,
        JwtHelperService,
        UiSettingsService,
        SettingsService,
        TranslateService,
        ThemeSettingsService, Object, Document, Object])
], SessionService);
export { SessionService };
